/**
 * The kinds of plan the create dialog picks from.
 *
 * Backed by TanStack Query rather than fetched fresh with every opening of
 * the dialog: the list barely changes, so a workspace that has opened it once
 * this session gets it back instantly on every later opening, and only a
 * quiet background read checks whether it has moved.
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchMealPlanTypes } from "../services/mealPlansService";
import type { MealPlanTypeOption } from "../types/plans.types";

export type MealPlanTypesStatus = "loading" | "ready" | "failed";

export interface MealPlanTypesState {
  options: MealPlanTypeOption[];
  status: MealPlanTypesStatus;
}

const NO_TYPES: MealPlanTypeOption[] = [];

export const MEAL_PLAN_TYPES_QUERY_KEY = ["plans", "meal-plan-types"] as const;

export function useMealPlanTypes(): MealPlanTypesState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: MEAL_PLAN_TYPES_QUERY_KEY,
    queryFn: ({ signal }) => fetchMealPlanTypes(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) console.error("Plan types failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    options: query.data ?? NO_TYPES,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
  };
}
